import { Upload, Button, message, Space, UploadProps } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useUploadCsvFile } from "hooks/mutations/csv";
import { translate, translateWithVars } from "shared/languages";
import { useAuth } from "context/AuthContextProvider";
import { isManager, isSystemAdmin } from "shared/utils/role";
import { showConfirmModal } from "components/modals/confirmModal";

// Data rows in a CSV file: records after the header, blank lines skipped, a
// quoted field may span lines. Only feeds the confirm's count.
export const countCsvRows = (text: string): number => {
  let rows = 0;
  let inQuotes = false;
  let hasContent = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      hasContent = true;
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (hasContent) rows++;
      hasContent = false;
      if (ch === "\r" && text[i + 1] === "\n") i++;
    } else if (ch.trim() !== "" && ch !== "﻿") {
      hasContent = true;
    }
  }
  if (hasContent) rows++;
  return Math.max(0, rows - 1);
};

// Which list the import fills, named in the confirm.
const TARGET_KEY: Record<string, string> = {
  customer: "csv_import.target_customer",
  staff: "csv_import.target_staff",
  class: "csv_import.target_class",
};

// templateUrl: optional public asset with the expected columns + example
// rows, rendered as a download link beside the upload button.
const FileUpload = ({
  endpoint,
  templateUrl,
}: {
  endpoint: string;
  templateUrl?: string;
}) => {
  const { mutate: upload, isPending } = useUploadCsvFile();
  // CSV import is Manager-only outright (Pembatasan Admin's fixed list; the
  // three /csv routes carry isManagerMiddleware). One check here covers the
  // Klien, Instruktur and Kelas toolbars; the template link goes with it.
  const { state } = useAuth();
  const canImport =
    isManager(state.user?.roles) || isSystemAdmin(state.user?.roles);
  if (!canImport) return null;

  // Picking a file no longer sends it straight away (System Review C4): the
  // admin confirms first, seeing how many rows it holds, and the button stays
  // disabled while an import runs, so a double click or a second pick of the
  // same file can't import it twice.
  const confirmUpload = async (file: Parameters<NonNullable<UploadProps["beforeUpload"]>>[0]) => {
    let rows = 0;
    try {
      rows = countCsvRows(await file.text());
    } catch {
      // Unreadable here: the server reports the file itself.
    }
    const target = translate(TARGET_KEY[endpoint] ?? "csv_import.target_customer");
    showConfirmModal({
      tone: "amber",
      title: translateWithVars(
        rows === 1 ? "csv_import.confirm_title_one" : "csv_import.confirm_title",
        { n: rows, target },
      ),
      content: file.name,
      confirmLabel: translate("csv_import.confirm_button"),
      onConfirm: () => upload({ endpoint, file }),
    });
  };

  const props: UploadProps = {
    beforeUpload: (file) => {
      const isCSV = file.type === "text/csv";

      if (!isCSV) {
        message.error(`${file.name} ${translate("messages.error.not_csv_file")}`);
        return Upload.LIST_IGNORE;
      }
      if (!isPending) void confirmUpload(file);
      // Never AntD's own upload: the mutation above sends the file.
      return Upload.LIST_IGNORE;
    },
    accept: ".csv",
    maxCount: 1,
    showUploadList: false,
    disabled: isPending,
  };

  return (
    <Space size={4}>
      {templateUrl && (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          href={templateUrl}
          download
        >
          {translate("csv_import.template_link")}
        </Button>
      )}
      <Upload {...props}>
        <Button icon={<UploadOutlined />} loading={isPending} disabled={isPending}>
          {translate("button.upload")} CSV
        </Button>
      </Upload>
    </Space>
  );
};

export default FileUpload;
